import {
  ShieldCheck,
} from "lucide-react";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { adminNavMain, adminNavSecondary } from "@/lib/navigation";
import { canManageRefundsUser, isTemplateAdminRole } from "@/lib/auth";
import { api } from "@/lib/api";
import { NavMain } from "@/components/navigation/nav-main";
import { NavSecondary } from "@/components/navigation/nav-secondary";
import { NavUser } from "@/components/navigation/nav-user";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import type { AuthUser } from "@/types/auth";
import type { AdminNotificationStatsResponse } from "@/types/admin-notifications";

// Roles permitted to view the user directory. Mirrors USER_VIEW_ROLES on
// the backend (admin-users.ts) — keep in sync. Showing the link to other
// roles just leads to a 403 + "Permission required" card.
const USER_DIRECTORY_ROLES = new Set(["SUPER_ADMIN", "ADMIN", "FOUNDER"]);

// Roles permitted to manage staff accounts, role definitions, and view
// the activity log. CC is intentionally excluded per rep feedback —
// the backend already 403s these routes for CC, but the sidebar
// previously rendered the links so clicking landed on a permission
// error page.
const STAFF_AND_ROLES_ROLES = new Set(["SUPER_ADMIN", "ADMIN", "FOUNDER"]);
const ACTIVITY_LOG_ROLES = new Set(["SUPER_ADMIN", "ADMIN", "FOUNDER", "ACCOUNTANT"]);

export function AdminSidebar({ user }: { user: AuthUser }) {
  const canViewUserDirectory = USER_DIRECTORY_ROLES.has(user.role);
  const canViewStaffAndRoles = STAFF_AND_ROLES_ROLES.has(user.role);
  const canViewActivityLog = ACTIVITY_LOG_ROLES.has(user.role);

  const secondaryItems = adminNavSecondary.filter((item) => {
    if (user.role === "FOUNDER") {
      return item.url === "/admin/settings";
    }
    if (item.url === "/admin/document-templates") {
      return isTemplateAdminRole(user.role);
    }
    if (item.url === "/admin/users") {
      return canViewUserDirectory;
    }
    if (item.url === "/admin/staff") {
      return canViewStaffAndRoles;
    }
    if (item.url === "/admin/roles") {
      return user.role === "SUPER_ADMIN";
    }
    if (item.url === "/admin/activity-log") {
      return canViewActivityLog;
    }

    return true;
  });

  const { data: notifStats } = useQuery({
    queryKey: ["notification-stats", "admin"],
    queryFn: async () => {
      try {
        const response = await api.get<AdminNotificationStatsResponse>(
          "/admin/notifications/stats"
        );
        return response.data;
      } catch {
        return { total: 0, unread: 0, important: 0, recent: 0 };
      }
    },
    refetchInterval: 30000,
    retry: false,
  });

  const unreadCount = notifStats?.unread ?? 0;

  const mainNavItems = useMemo(
    () =>
      adminNavMain
        .filter((item) => {
          if (item.url === "/admin/founder-review") return user.role === "FOUNDER";
          if (item.url === "/admin/users") return canViewUserDirectory;
          if (item.url === "/admin/refunds") return canManageRefundsUser(user);
          return true;
        })
        .map((item) => {
          if (item.url === "/admin/notifications") {
            return { ...item, badge: unreadCount };
          }
          return item;
        }),
    [unreadCount, canViewUserDirectory, user],
  );

  return (
    <Sidebar variant="inset">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <div className="flex items-center gap-2 p-2">
              <img
                src="/images/landnest-logo-green.png"
                alt="Landnest Homes and Properties"
                className="h-8 w-auto object-contain"
              />
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <div className="px-2 pt-2">
          <NavUser user={user} />
        </div>
        <NavMain items={mainNavItems} />
        <NavSecondary items={secondaryItems} className="mt-auto" />
      </SidebarContent>
      <SidebarFooter>
        <div className="text-sidebar-foreground/70 flex items-center gap-2 rounded-lg border border-sidebar-border/70 bg-sidebar-accent/60 px-3 py-2 text-xs">
          <ShieldCheck className="size-4" />
          Protected by the new Hono auth boundary
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
